import { useState } from 'react';
import { useParams } from 'react-router-dom';
import RebusDashboard from '../components/rebus/host/RebusDashboard';
import RebusCreator from '../components/rebus/host/RebusCreator';
import RebusSessionControl from '../components/rebus/host/RebusSessionControl';
import { RebusSessionProvider } from '../context/RebusSessionContext';
import { createRebusSession } from '../services/rebusSessionService';
import { getRebus } from '../services/rebusService';

export default function RebusHostPage() {
  const { sessionId: urlSessionId } = useParams();
  const [view, setView] = useState(urlSessionId ? 'session' : 'dashboard');
  const [activeSessionId, setActiveSessionId] = useState(urlSessionId || null);
  const [editingRebus, setEditingRebus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleCreateNew = () => {
    setEditingRebus(null);
    setView('create');
  };

  const handleEditRebus = (rebus) => {
    setEditingRebus(rebus);
    setView('create');
  };

  const handleStartSession = async (rebusId) => {
    setLoading(true);
    setError(null);

    try {
      const session = await createRebusSession(rebusId);
      setActiveSessionId(session.id);
      setView('session');
    } catch (err) {
      console.error('Error creating rebus session:', err);
      setError(err.message || 'Klarte ikkje opprette Mikkelchase-okt');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenSession = (sessionId) => {
    setActiveSessionId(sessionId);
    setView('session');
  };

  const handleSaveRebus = () => {
    setEditingRebus(null);
    setView('dashboard');
  };

  const handleBackToDashboard = () => {
    setEditingRebus(null);
    setActiveSessionId(null);
    setView('dashboard');
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-background-light font-display flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mx-auto mb-4"></div>
          <h2 className="text-xl font-semibold text-gray-600">Opprettar Mikkelchase-okt...</h2>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-background-light font-display flex items-center justify-center p-4">
        <div className="bg-red-50 shadow-ambient rounded-2xl p-6 max-w-md w-full">
          <h2 className="text-xl font-bold text-red-800 mb-2">Feil</h2>
          <p className="text-red-700 mb-4">{error}</p>
          <button
            onClick={() => {
              setError(null);
              setView('dashboard');
            }}
            className="w-full px-4 py-3 bg-gradient-to-r from-eq-primary to-eq-primary-container hover:brightness-110 text-white rounded-xl font-bold transition-all"
          >
            Tilbake til oversikta
          </button>
        </div>
      </div>
    );
  }

  // Session view
  if (view === 'session' && activeSessionId) {
    return (
      <RebusSessionProvider sessionId={activeSessionId}>
        <RebusSessionControl onBack={handleBackToDashboard} />
      </RebusSessionProvider>
    );
  }

  // Creator view
  if (view === 'create') {
    return (
      <RebusCreator
        onSave={handleSaveRebus}
        onClose={handleBackToDashboard}
        existingRebus={editingRebus}
      />
    );
  }

  // Dashboard (default)
  return (
    <RebusDashboard
      onCreateNew={handleCreateNew}
      onStartSession={handleStartSession}
      onEditRebus={handleEditRebus}
      onOpenSession={handleOpenSession}
    />
  );
}
